import { type RefObject, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Grid } from '@mui/material'

import ListItemWrapper from '~/components/dashboard/soapV2/order/ListItemWrapper'
import OrderTooltip from '~/components/dashboard/soapV2/order/OrderTooltip'
import ReadonlyOrderTooltip from '~/components/dashboard/soapV2/order/ReadonlyOrderTooltip'
import FeatureToggle from '~/constants/featureToggle'
import InvoiceType from '~/constants/InvoiceType'
import { convertToLineItems } from '~/store/actions/finance'
import { editOrder } from '~/store/actions/orders'
import { registerWarnAlert } from '~/store/duck/uiAlerts'
import { useFetchPrescriptionTemplate } from '~/store/hooks/prescriptionTemplates'
import { getFeatureToggle } from '~/store/reducers/constants'
import {
  getAppointmentLineItemsMap,
  getFinanceIsConvertingToLineItems,
} from '~/store/reducers/finance'
import { getSelectedOrders } from '~/store/reducers/orders'
import type {
  BaseChargeTabProps,
  Estimate,
  Order,
  OrderCallbackWithTypeCheck,
  OrderSearchFilter,
  Prescription,
} from '~/types'
import {
  convertOrderToInvoiceLineItem,
  findOrderItemByItem,
  getItemColor,
  isObsoleteGlobalItem,
} from '~/utils/orderUtils'
import useGetIsCatalogSelectableItem from '~/utils/useGetIsCatalogSelectableItem'

import { useEstimateCheckAllItems } from './hooks/useEstimateCheckAllItems'
import { useEstimateCheckItem } from './hooks/useEstimateCheckItem'
import { useEstimateIsCheckedItem } from './hooks/useEstimateIsCheckedItem'
import { useEstimateSelectedItems } from './hooks/useEstimateSelectedItems'
import { useEstimateUncheckItem } from './hooks/useEstimateUncheckItem'

export interface EstimateSearchResultItemProps extends Omit<
  BaseChargeTabProps,
  'enableBackendFiltering'
> {
  clientId: string
  contentRef: RefObject<any>
  disabledWeakMap: WeakMap<Order, boolean>
  estimate: Estimate
  isReceivingOrders: boolean
  onSelect: (itemId: string | undefined, meta?: Record<string, any>) => void
  onUpdateOrder?: (order: Order) => void
  order: Order
  patientId: string
  searchResultFilter?: OrderSearchFilter[]
  searchTerm: string
  updateInvoice?: (invoice: Estimate) => void
}

const EstimateSearchResultItem = ({
  estimate,
  clientId,
  contentRef,
  disabled,
  disabledWeakMap,
  isReceivingOrders,
  logItemStateGetter,
  order,
  patientId,
  searchResultFilter,
  searchTerm,
  onSelect,
  onUpdateOrder,
  updateInvoice,
}: EstimateSearchResultItemProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['Search', 'Common'])

  const appointmentLineItemsMap = useSelector(getAppointmentLineItemsMap)
  const selectedOrders = useSelector(getSelectedOrders)
  const isLoading = useSelector(getFinanceIsConvertingToLineItems)
  const isFoodCatalogEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )

  // State for prescription template loading
  const [loadingTemplateOrder, setLoadingTemplateOrder] =
    useState<Order | null>(null)

  // Load prescription templates for estimates
  const [loadPrescriptionTemplates] = useFetchPrescriptionTemplate({
    patientId,
    onTemplateFetched: (template, fetchedOrder) => {
      // fetchedOrder is the ORIGINAL order with price preserved
      // Only update notes from template, keep everything else
      const orderWithNotes = {
        ...fetchedOrder,
        notes: template ? template.notes : fetchedOrder.notes,
      } as Order

      // Ensure usedQuantity is set before passing to logItemStateGetter
      const orderToAdd =
        orderWithNotes.usedQuantity === 0
          ? { ...orderWithNotes, usedQuantity: 1 }
          : orderWithNotes

      // After loading template, add the prescribed item to the estimate
      const preparedOrder = logItemStateGetter(orderToAdd)
      if (preparedOrder) {
        // Verify the order has a price before converting
        if (!preparedOrder.price || !preparedOrder.price.id) {
          // Show error notification to user
          dispatch(
            registerWarnAlert(t('Dialogs:INVOICE_DIALOG.LINE_ITEM_NO_PRICE')),
          )
          setLoadingTemplateOrder(null)
          return
        }

        const lineItem = convertOrderToInvoiceLineItem(
          preparedOrder,
          isFoodCatalogEnabled,
        )

        dispatch(
          convertToLineItems({
            invoiceId: estimate.id,
            clientId,
            patientId,
            invoiceType: InvoiceType.ESTIMATE.toUpperCase(),
            otcItems: [lineItem],
            // Use discount only for bundles
            bundleDiscount: 0,
          }),
        )
      }

      setLoadingTemplateOrder(null)

      // Notify parent if needed
      if (onUpdateOrder) {
        onUpdateOrder(orderWithNotes)
      }
    },
  })

  // Handle prescription template loading
  const handleLoadPrescriptionTemplate = (prescriptionOrder: Order) => {
    setLoadingTemplateOrder(prescriptionOrder)
    loadPrescriptionTemplates(prescriptionOrder as Prescription)
  }

  // Hooks for checking item state
  const isCheckedItem = useEstimateIsCheckedItem({ estimate })

  // Combine disabled states: base disabled + loading template + pending add/remove
  const combinedDisabledWeakMap = new WeakMap<Order, boolean>()
  if (disabledWeakMap) {
    // Copy entries from existing WeakMap - need to iterate through known orders
    const allOrders = [order]
    allOrders.forEach((o) => {
      const isDisabled = disabledWeakMap.get(o)
      if (isDisabled !== undefined) {
        combinedDisabledWeakMap.set(o, isDisabled)
      }
    })
  }
  // Disable the current order if its template is loading
  if (loadingTemplateOrder && loadingTemplateOrder === order) {
    combinedDisabledWeakMap.set(order, true)
  }

  const {
    isSelectedItem,
    selectedSubItem,
    setSelectedSubItem,
    updateSelectedItem,
  } = useEstimateSelectedItems()
  const handleCheckItem = useEstimateCheckItem({
    estimate,
    clientId,
    patientId,
    logItemStateGetter,
    onSelectItem: onSelect,
    loadPrescriptionTemplate: handleLoadPrescriptionTemplate,
  })
  const handleCheckAllListItems = useEstimateCheckAllItems({
    estimate,
    clientId,
    patientId,
  })
  const handleListItemUncheck = useEstimateUncheckItem({
    estimate,
    updateInvoice: updateInvoice || (() => {}),
  })

  const isNotObsolete = !isObsoleteGlobalItem(order, isFoodCatalogEnabled)
  const toolTipText = isNotObsolete ? (
    <ReadonlyOrderTooltip order={order} />
  ) : (
    t('Common:OBSOLETE')
  )

  const getIsCatalogSelectableItem = useGetIsCatalogSelectableItem()

  const isSelectableItem = getIsCatalogSelectableItem(order)

  const onCheckItem = (
    currentOrder: Order,
    checkOptions?: {
      isDrug?: boolean
      isFood?: boolean
      isPrescribable?: boolean
      isVetDiet?: boolean
    },
  ) => {
    handleCheckItem(currentOrder, checkOptions)
  }

  const onUncheckItem: OrderCallbackWithTypeCheck = (currentOrder) => {
    handleListItemUncheck(currentOrder, false)
  }

  const onCheckAll = (currentOrder: Order, checked: boolean) => {
    if (checked) {
      handleCheckAllListItems(currentOrder, checked)
    } else {
      // Unchecking entire bundle via "Select All" button
      handleListItemUncheck(currentOrder, true)
    }
  }

  const onUpdateItem = (newOrder: Order) => {
    const fullOrder = findOrderItemByItem(newOrder, selectedOrders)
    dispatch(editOrder({ ...fullOrder, ...newOrder }))
  }

  return (
    <Grid>
      <ListItemWrapper
        editDisabled
        includeOnHandQuantity
        includePrice
        showTooltip
        useChargesWidgetView
        ItemTooltipComponent={OrderTooltip}
        clientId={clientId}
        disabledWeakMap={combinedDisabledWeakMap}
        getItemColor={getItemColor(
          appointmentLineItemsMap || {},
          selectedOrders,
        )}
        isCheckedItem={isCheckedItem}
        isDisabled={
          disabled || combinedDisabledWeakMap?.get(order) || isLoading
        }
        isLoading={isReceivingOrders}
        isSelectableItem={isSelectableItem}
        isSelected={isSelectedItem(order)}
        order={order}
        patientId={patientId}
        ref={contentRef}
        searchResultFilter={searchResultFilter}
        searchTerm={searchTerm}
        selectedSubItem={selectedSubItem}
        toolTipText={toolTipText}
        updateSelectedItem={updateSelectedItem}
        updateSelectedSubItem={setSelectedSubItem}
        onCheckAll={onCheckAll}
        onCheckItem={onCheckItem}
        onUncheckItem={onUncheckItem}
        onUpdateItem={onUpdateItem}
      />
    </Grid>
  )
}

export default EstimateSearchResultItem
